import { useRouter } from 'next/router'
import { memo } from 'react'
import { tv } from 'tailwind-variants'

import { SupportedMarketKey, getLocaleParts } from '@nxt-insurance/constants'
import { SCREEN_SIZE, SPACE } from '@nxt-insurance/ui-kit/constants'

import { runQuery } from '@/cms/client'
import { routeQuery, siteSettingsQueryNavbar } from '@/cms/pages'
import { APP_ENV, DOMAIN } from '@/config/env'
import { useWindowWidth } from '@/hooks/useWindowWidth'
import { getFromCookiesOrUrlParams, queryToSearchParams } from '@/utils/cookies'
import { getNavbarFromSplit } from '@/utils/page'
import { useQuery } from '@tanstack/react-query'

import { HeaderCtaButton } from './HeaderCta'
import PromotionModal from './PromotionModal'

const pageWrapper = tv({
  slots: {
    base: 'mx-auto flex min-h-screen w-full items-center pt-14 lg:pt-[72px]',
    headerCtaButton: 'fixed right-0 z-50 mx-6 my-4 max-xl:bottom-0 xl:top-0',
  },
  variants: {
    isProductFlowPage: {
      // Needed for the header cta button in landing pages
      // to be placed correctly but not needed for product flow pages
      // as they don't include the header cta button
      false: {
        base: 'flex-col',
      },
    },
    hasCampaign: {
      true: {
        base: 'pt-[100px] lg:pt-[116px]',
        headerCtaButton: 'pt-10 lg:pt-12',
      },
    },
  },
})

export const PageWrapper = memo(({ children }: { children: React.ReactNode }) => {
  const { query, asPath, locale } = useRouter()
  const slug = query.slug?.[1] ?? 'index'
  const folder = query.slug?.[0] ?? null

  const [lang, market] = getLocaleParts(locale)
  const isProductFlowPage = asPath.includes('/flows/')

  const { data: campaign } = useQuery({
    queryKey: ['campaign', asPath, query],
    queryFn: async () => {
      const campaignFromQueryOrCookies = getFromCookiesOrUrlParams(
        queryToSearchParams(query)
      ).campaign
      if (campaignFromQueryOrCookies) {
        return campaignFromQueryOrCookies
      }
      const result = await fetch(
        `${APP_ENV !== 'development' ? 'https://' : 'http://'}${DOMAIN}/api/campaign`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      const json = await result.json()
      return json.campaign ?? null
    },
  })

  const { data: route } = useQuery({
    queryKey: ['route', query, asPath],
    queryFn: async () => runQuery(routeQuery, { lang, market, slug, folder }),
  })

  const { data: navbar } = useQuery({
    queryKey: ['siteSettingsNavbar'],
    queryFn: async () => {
      const siteSettingsNavbar = await runQuery(siteSettingsQueryNavbar, { lang, market })
      return await getNavbarFromSplit(siteSettingsNavbar.navbar, lang)
    },
    // Enable site settings query only for landing pages
    // and not for product flow pages as the latter
    // doesn't include navbar
    enabled: !isProductFlowPage,
  })

  const styles = pageWrapper({
    isProductFlowPage,
    hasCampaign: Boolean(campaign) || market === SupportedMarketKey.De,
  })

  const windowWidth = useWindowWidth()
  const ctaButtonOffset = (windowWidth - SCREEN_SIZE.XXL) / 2 - SPACE.small
  const ctaRightPlacement = windowWidth >= SCREEN_SIZE.XXL ? ctaButtonOffset : 0

  return (
    <div className={styles.base()}>
      {navbar?.ctaItems ? (
        <div
          className={styles.headerCtaButton()}
          style={{
            right: ctaRightPlacement,
          }}
        >
          {navbar.ctaItems.map((ctaItem) => (
            <HeaderCtaButton
              key={ctaItem._id}
              pageType={route?.pageType ?? undefined}
              cta={ctaItem}
            />
          )) ?? null}
        </div>
      ) : null}
      <PromotionModal />
      {children}
    </div>
  )
})
